import { Component, OnInit, ViewChild, inject, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { RouterModule } from '@angular/router';
import { BankStore, ContactStore, InsuranceStore } from '../core/stores/base.store';
import { parseYYYYMMDDToDate } from '../core/utils/date.utils';
import { MatTableModule } from '@angular/material/table';
import { Subscription } from 'rxjs';

interface SummaryData {
  totalBanks: number;
  totalLoans: number;
  totalContacts: number;
  totalInsurance: number;
  bankBalance: number;
  totalLoanBalance: number;
  totalPrincipal: number;
  totalInterest: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    RouterModule,
    MatTableModule
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnDestroy {
  // Inject stores
  bankStore = inject(BankStore);
  contactStore = inject(ContactStore);
  insuranceStore = inject(InsuranceStore);

  banks = this.bankStore.entities;
  contacts = this.contactStore.entities;
  insurances = this.insuranceStore.entities;

  // Track loading state
  banksLoaded = this.bankStore.loaded;
  contactsLoaded = this.contactStore.loaded;
  insurancesLoaded = this.insuranceStore.loaded;
  
  // Track if all data is loaded
  allDataLoaded = false;

  summaryData: SummaryData = {
    totalBanks: 0,
    totalLoans: 0,
    totalContacts: 0,
    totalInsurance: 0,
    bankBalance: 0,
    totalLoanBalance: 0,
    totalPrincipal: 0,
    totalInterest: 0
  };

  // Insurance metrics
  activeInsurancePolicies: number = 0;
  upcomingRenewals: number = 0;
  
  // Insurance policies grouped by insured name
  insuranceSummaries: {
    insuredName: string;
    policies: number;
    totalInsurance: number;
    totalPremium: number;
    totalCashValue: number;
    totalLoanAmount: number;
  }[] = [];

  // Loans grouped by contact name
  loanSummaries: {
    contactName: string;
    loans: number;
    totalAmount: number;
    totalRemainingBalance: number;
    totalPayment: number;
    volumeRateOfReturn: number; // Changed from interestRate to volumeRateOfReturn
  }[] = [];

  displayedColumns: string[] = ['date', 'name', 'category', 'amount'];
  
  // Cleanup subscriptions
  private subscriptions: Subscription[] = [];

  constructor() {
    // Create an effect to monitor when all stores are loaded
    effect(() => {
      const banksLoaded = this.banksLoaded();
      const contactsLoaded = this.contactsLoaded();
      const insurancesLoaded = this.insurancesLoaded();
      
      // Check if all stores are loaded
      if (banksLoaded && contactsLoaded && insurancesLoaded) {
        this.allDataLoaded = true;
        // Calculate all dashboard data once everything is loaded
        this.calculateSummaryData();
        this.calculateInsuranceSummaries();
        this.calculateLoanSummaries();
      }
    });
  }
  
  ngOnDestroy(): void {
    // Clean up any subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  calculateSummaryData(): void {
    const banks = this.banks();
    const contacts = this.contacts();
    const insurances = this.insurances();
    
    // Calculate totals
    this.summaryData.totalBanks = banks.length;
    this.summaryData.totalContacts = contacts.length;
    this.summaryData.totalInsurance = insurances.length;
    
    // Calculate loan totals and transaction totals
    let totalLoans = 0;
    let totalPrincipal = 0;
    let totalInterest = 0;
    let totalLoanBalance = 0;
    
    banks.forEach(bank => {
      if (bank.loans_collection) {
        totalLoans += bank.loans_collection.length;
      }
      
      // Calculate principal and interest from bank transactions
      if (bank.bank_transactions_collection) {
        bank.bank_transactions_collection.forEach(transaction => {
          if (transaction.loan_id && transaction.loan_id > 0) {
            totalPrincipal += Number(transaction.principal) || 0;
            totalInterest += Number(transaction.interest) || 0;
          }
        });
      }
    });
    
    totalLoanBalance = totalPrincipal + totalInterest;
    
    this.summaryData.totalLoans = totalLoans;
    this.summaryData.totalPrincipal = totalPrincipal;
    this.summaryData.totalInterest = totalInterest;
    this.summaryData.totalLoanBalance = totalLoanBalance;
    
    // Calculate bank balance
    let bankBalance = 0;
    banks.forEach(bank => {
      const startingBalance = Number(bank.starting_balance) || 0;
      const transactionsSum = bank.bank_transactions_collection?.reduce(
        (sum, transaction) => sum + Number(transaction.amount || 0), 0
      ) || 0;
      bankBalance += startingBalance + transactionsSum;
    });
    this.summaryData.bankBalance = bankBalance;
  }

  getContactName(contactId: number): string {
    if (!contactId) return 'Unknown';
    
    const contact = this.contacts().find(c => c.id === contactId);
    if (!contact) return 'Unknown';
    
    return contact.first_name + ' ' + contact.last_name;
  }

  calculateInsuranceSummaries(): void {
    const insurances = this.insurances();
    if (!insurances || insurances.length === 0) {
      this.insuranceSummaries = [];
      return;
    }

    // Group insurances by insured name
    const insuranceByInsured = new Map<string, {
      policies: number;
      totalInsurance: number;
      totalPremium: number;
      totalCashValue: number;
      totalLoanAmount: number;
    }>();

    // Process each insurance policy
    insurances.forEach(insurance => {
      const insuredName = this.getContactName(insurance.contact_id) || 'Unknown';
      
      // Get or initialize summary for this insured
      if (!insuranceByInsured.has(insuredName)) {
        insuranceByInsured.set(insuredName, {
          policies: 0,
          totalInsurance: 0,
          totalPremium: 0,
          totalCashValue: 0,
          totalLoanAmount: 0
        });
      }
      
      const summary = insuranceByInsured.get(insuredName)!;
      
      // Increment policy count
      summary.policies += 1;
      
      // Add base insurance amount
      summary.totalInsurance += Number(insurance.face_value || 0);
      
      // Add base premium
      summary.totalPremium += Number(insurance.premium || 0);
      
      // Add base cash value
      summary.totalCashValue += Number(insurance.cash_value || 0);
      
      // Add rider amounts
      if (insurance.riders_collection && insurance.riders_collection.length > 0) {
        insurance.riders_collection.forEach(rider => {
          summary.totalInsurance += Number(rider.face_value || 0);
          summary.totalPremium += Number(rider.premium || 0);
          summary.totalCashValue += Number(rider.cash_value || 0);
        });
      }
      
      // Add loan amounts
      if (insurance.insurance_loans_collection && insurance.insurance_loans_collection.length > 0) {
        insurance.insurance_loans_collection.forEach(loan => {
          // Only count active loans
          if (loan.state === 'active') {
            summary.totalLoanAmount += Number(loan.amount || 0);
          }
        });
      }
      
      // Update the map
      insuranceByInsured.set(insuredName, summary);
    });
    
    // Convert map to array
    this.insuranceSummaries = Array.from(insuranceByInsured.entries())
      .map(([insuredName, summary]) => ({
        insuredName,
        ...summary
      }))
      .sort((a, b) => b.totalInsurance - a.totalInsurance); // Sort by total insurance amount
  }

  calculateLoanSummaries(): void {
    const banks = this.banks();
    const contacts = this.contacts();
    
    if (!banks || banks.length === 0 || !contacts || contacts.length === 0) {
      this.loanSummaries = [];
      return;
    }

    // Group loans by contact id
    const loansByContact = new Map<number, {
      contactName: string;
      loans: number;
      totalAmount: number;
      totalRemainingBalance: number;
      totalPayment: number;
      totalInterestAmount: number; // We'll use this to calculate volume rate of return
    }>();

    // Process each bank's loans
    banks.forEach(bank => {
      if (bank.loans_collection && bank.loans_collection.length > 0) {
        bank.loans_collection.forEach(loan => {
          // Only include active loans
          if (loan.state === 'active') {
            const contactId = loan.contact_id;
            const contact = contacts.find(c => c.id === contactId);
            const contactName = contact 
              ? `${contact.first_name} ${contact.last_name}` 
              : `Contact ${contactId}`;
            
            // Get or initialize summary for this contact
            if (!loansByContact.has(contactId)) {
              loansByContact.set(contactId, {
                contactName,
                loans: 0,
                totalAmount: 0,
                totalRemainingBalance: 0,
                totalPayment: 0,
                totalInterestAmount: 0, // Changed from interestRateSum to totalInterestAmount
              });
            }
            
            const summary = loansByContact.get(contactId)!;
            
            // Increment loan count
            summary.loans += 1;
            
            // Add loan amount
            summary.totalAmount += Number(loan.amount || 0);
            
            // Add remaining balance
            summary.totalRemainingBalance += Number(loan.remaining_balance || 0);
            
            // Add payment
            summary.totalPayment += Number(loan.payment || 0);
            
            // Calculate interest amount (payment * term - amount) for volume rate of return
            const loanAmount = Number(loan.amount || 0);
            const payment = Number(loan.payment || 0);
            const term = Number(loan.term || 0);
            const totalPayments = payment * term;
            const interestAmount = totalPayments - loanAmount;
            
            // Add to total interest amount for VRR calculation
            summary.totalInterestAmount += interestAmount > 0 ? interestAmount : 0;
            
            // Update the map
            loansByContact.set(contactId, summary);
          }
        });
      }
    });
    
    // Convert map to array and calculate average interest rate
    this.loanSummaries = Array.from(loansByContact.entries())
      .map(([_, summary]) => ({
        contactName: summary.contactName,
        loans: summary.loans,
        totalAmount: summary.totalAmount,
        totalRemainingBalance: summary.totalRemainingBalance,
        totalPayment: summary.totalPayment,
        // Calculate Volume Rate of Return: Total Interest / Total Loan Amount
        volumeRateOfReturn: summary.totalAmount > 0 ? summary.totalInterestAmount / summary.totalAmount : 0
      }))
      .sort((a, b) => b.totalAmount - a.totalAmount); // Sort by total loan amount
  }

  getRecentTransactions(): void {    
    // Only proceed if stores are loaded
    if (!this.banksLoaded() || !this.contactsLoaded()) {
      return;
    }
    
    const banks = this.banks();
    const contacts = this.contacts();
    const transactions: any[] = [];
        
    // Get bank transactions
    banks.forEach(bank => {
      if (!bank) return;
            
      if (bank.bank_transactions_collection && Array.isArray(bank.bank_transactions_collection)) {        
        bank.bank_transactions_collection.forEach(transaction => {
          if (!transaction) return;
          
          try {
            // Ensure transaction_date is a string before parsing
            let transactionDate: Date | null = null;
            let originalDateString = '';
            
            if (typeof transaction.transaction_date === 'string' && transaction.transaction_date.trim() !== '') {
              try {
                // Parse the date for sorting
                transactionDate = parseYYYYMMDDToDate(transaction.transaction_date);
                
                // Extract date components directly from the string for display
                const [year, month, day] = transaction.transaction_date.split('-').map(Number);
                originalDateString = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
              } catch (error) {
                console.error('Error parsing transaction date:', error);
                transactionDate = new Date(); // Fallback to current date
                originalDateString = transaction.transaction_date; // Use original string
              }
            } else {
              transactionDate = new Date(); // Default to current date if no valid date
              originalDateString = '';
            }
            
            // Ensure amount is a number
            const amount = Number(transaction.amount || 0);
            
            const processedTransaction = {
              date: transactionDate,
              displayDate: originalDateString,
              source: 'bank',
              sourceName: bank.name || 'Unknown Bank',
              category: transaction.category || 'Unknown',
              amount: amount
            };
            
            transactions.push(processedTransaction);
          } catch (error) {
            console.error('Error processing bank transaction:', error, transaction);
          }
        });
      } 
    });
  }
}
